import { useNavigate } from 'react-router-dom';
import { useSubscriptionPath } from '../api/public/useSubscriptionPath';
import { useEligibilityRequest } from '../api/onboarding/mutations/useEligibilityRequest';
import { mergeSearchParams } from '../utils/searchParams';
import { TRACKING_ELIGIBILITY_CHECK_VERIFIED } from '../components/OnboardingForm/constants';
import { useTracking } from './useTracking';

export const useEligibilityRouting = ({
  userId,
  state,
  search,
  enable = false,
}: {
  userId: string;
  state?: any;
  search?: string;
  enable?: boolean;
}) => {
  const navigate = useNavigate();
  const { track } = useTracking();
  const { mutation } = useEligibilityRequest();

  const { isLoading: isSubscriptionPathLoading } = useSubscriptionPath({
    userId,
    enable,
    onSuccess: async (data) => {
      const { affiliate, referrer, email, firstName, lastName } = state || {};

      if (data.subscription_path === 'out-of-pocket') {
        navigate(`/plans${search}`, {
          state: {
            affiliate,
            referrer,
            userId,
            userEmail: email,
          },
        });
      } else if (data.subscription_path === 'eligibility-check') {
        try {
          const { route_path, message } = await mutation.mutateAsync({
            user_id: userId,
            user_email: email,
            first_name: firstName,
            last_name: lastName,
            relationship_to_subscriber: 'self',
          } as any);

          if (route_path === 'ineligible') {
            navigate(`/ineligible${search}`, {
              state: {
                message,
                userId,
              },
            });
          } else if (route_path === 'book-a-call') {
            // The same event the full eligibility form sends, so Braze sends
            // its welcome message however the check was passed. This branch is
            // the one we already treat as verified, which keeps the event and
            // the screen the user lands on from disagreeing.
            track(TRACKING_ELIGIBILITY_CHECK_VERIFIED, { userId });
            navigate(
              `/schedule-appointment${mergeSearchParams(search, { userId })}`,
              { state: { userEmail: email } }
            );
          } else {
            navigate(`/eligibility${search}`, {
              state: {
                affiliate,
                referrer,
                userId,
                userEmail: email,
                firstName,
                lastName,
              },
            });
          }
        } catch (error) {
          navigate(`/eligibility${search}`, {
            state: {
              affiliate,
              referrer,
              userId,
              userEmail: email,
              firstName,
              lastName,
            },
          });
        }
      } else if (data.subscription_path === 'book-a-call') {
        navigate(`/schedule-appointment?userId=${encodeURIComponent(userId)}`, {
          state: { userEmail: email },
        });
      }
    },
  });

  return { isPending: isSubscriptionPathLoading || mutation.isLoading };
};

export default useEligibilityRouting;
